import {
  UNLIMITED,
  firstPlanWith,
  type EntitlementsDto,
  type PlanFeature,
  type SubscriptionPlan,
} from '@osteopro/shared';
import { isApiError } from '../../lib/api.js';

/**
 * Lecture des droits côté écran. Fonctions pures : ce qui décide d'un verrou visible doit se
 * vérifier sans monter de composant, et surtout ne jamais **remplacer** la vérification du
 * serveur — la V1 cachait un bouton et laissait la route ouverte, ce qui n'est pas un quota.
 *
 * Tant que les droits ne sont pas chargés, **rien n'est verrouillé** : un écran qui se
 * verrouillerait le temps d'une requête ferait croire à une perte d'accès.
 */

export interface QuotaState {
  limit: number;
  used: number;
  unlimited: boolean;
  /** Reste disponible, jamais négatif. `null` quand le quota est sans limite. */
  remaining: number | null;
  /** Vrai quand la limite est atteinte ou dépassée : le prochain ajout sera refusé. */
  reached: boolean;
  /** Part consommée, de 0 à 100. `null` sans limite : une jauge n'aurait pas de fond. */
  percent: number | null;
}

export function quotaState(quota: { limit: number; used: number }): QuotaState {
  if (quota.limit === UNLIMITED) {
    return {
      limit: quota.limit,
      used: quota.used,
      unlimited: true,
      remaining: null,
      reached: false,
      percent: null,
    };
  }
  return {
    limit: quota.limit,
    used: quota.used,
    unlimited: false,
    remaining: Math.max(0, quota.limit - quota.used),
    reached: quota.used >= quota.limit,
    percent: quota.limit === 0 ? 100 : Math.min(100, Math.round((quota.used / quota.limit) * 100)),
  };
}

/**
 * Le cabinet est bloqué pour l'appelant : essai terminé ou abonnement expiré, tout est fermé sauf
 * la souscription et l'export du propriétaire (décision du 11 octobre 2026). Le champ s'appelle
 * encore `readOnly`. Tant que les droits ne sont pas chargés, non : on ne bloque pas sur une
 * ignorance.
 */
export function isBlocked(entitlements: EntitlementsDto | undefined): boolean {
  return entitlements?.readOnly === true;
}

/** Un levier reste ouvert tant qu'on ne sait pas : on ne verrouille pas sur une ignorance. */
export function hasFeature(
  entitlements: EntitlementsDto | undefined,
  feature: PlanFeature,
): boolean {
  return entitlements === undefined || entitlements.features[feature];
}

/**
 * Le serveur a refusé au nom de l'offre (`402 feature_not_in_plan`). L'écran qui le reçoit sur des
 * droits périmés — offre changée dans un autre onglet — bascule dans sa variante verrouillée au lieu
 * d'afficher une erreur.
 */
export function isPlanRefusal(error: unknown): boolean {
  return isApiError(error) && error.code === 'feature_not_in_plan';
}

/** L'offre à partir de laquelle le levier s'ouvre : ce que l'infobulle de verrouillage annonce. */
export function requiredPlanFor(feature: PlanFeature): SubscriptionPlan | null {
  return firstPlanWith(feature);
}

/** Même prudence pour les quotas : tant que la lecture n'a pas abouti, rien n'est atteint. */
export function quotaReached(
  entitlements: EntitlementsDto | undefined,
  quota: 'patients' | 'practitioners',
): boolean {
  if (entitlements === undefined) return false;
  return quotaState(entitlements.quotas[quota]).reached;
}
